import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto space-y-6 px-4 py-5">
      <Link to="/" className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-blue-50 text-blue-700">
          <ShieldCheck size={24} />
        </div>

        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">
          RevUShield
        </h1>
      </Link>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="border-b border-slate-200 pb-6 mb-6">
          <h1 className="text-3xl font-bold text-slate-900">
            Terms of Service
          </h1>

          <p className="text-slate-500 mt-2">Last updated: May 2026</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <p>
            These Terms of Service (“Terms”) govern your access to and use of
            RevUShield (“RevUShield,” “we,” “our,” or “us”), including our
            website, applications, and services. By creating an account,
            connecting a Google Business Profile, or using the Service, you
            agree to these Terms.
          </p>

          <p>If you do not agree to these Terms, do not use the Service.</p>

          <Section title="1. Service Description">
            <p>
              RevUShield is a software-as-a-service platform designed to help
              businesses manage and respond to Google Business Profile reviews
              using artificial intelligence.
            </p>

            <ul className="list-disc pl-5 space-y-2">
              <li>Syncing reviews from connected Google Business Profiles</li>
              <li>AI-generated review response suggestions</li>
              <li>Manual review and approval workflows</li>
              <li>Review response publishing tools</li>
              <li>Notifications and dashboard analytics</li>
              <li>Subscription and billing management</li>
            </ul>

            <p>Features may change over time as the platform evolves.</p>
          </Section>

          <Section title="2. Eligibility & Accounts">
            <p>To use RevUShield, you must:</p>

            <ul className="list-disc pl-5 space-y-2">
              <li>Be at least 18 years old</li>
              <li>
                Have authority to act on behalf of the business you connect
              </li>
              <li>Provide accurate registration information</li>
              <li>Maintain the security of your account credentials</li>
            </ul>

            <p>
              You are responsible for all activity that occurs under your
              account. You may only connect Google Business Profiles that you
              own or are authorized to manage.
            </p>
          </Section>

          <Section title="3. Google Account Connections">
            <p>
              RevUShield uses Google APIs to access approved Google Business
              Profile data. By connecting your Google account, you authorize
              RevUShield to access information permitted by the scopes you
              approve during authentication.
            </p>

            <p>
              RevUShield does not claim ownership of your Google Business
              Profile data. You may revoke access at any time through your
              Google account permissions settings.
            </p>

            <p>
              Certain features depend on third-party APIs and services,
              including Google Business Profile APIs. We are not responsible for
              outages, API limitations, quota restrictions, policy changes, or
              service interruptions caused by third-party providers.
            </p>
          </Section>

          <Section title="4. AI-Generated Responses">
            <p>
              RevUShield may generate suggested responses using artificial
              intelligence. You acknowledge and agree that AI-generated
              responses may contain inaccuracies, incomplete information, or
              inappropriate language.
            </p>

            <p>
              You are responsible for reviewing all generated content before
              publishing and are solely responsible for any responses posted to
              your Google Business Profile.
            </p>

            <p>
              You should not rely on AI-generated responses as legal,
              professional, or regulatory advice.
            </p>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to use RevUShield to:</p>

            <ul className="list-disc pl-5 space-y-2">
              <li>Violate any law or regulation</li>
              <li>Violate Google’s policies or terms</li>
              <li>
                Post false, misleading, defamatory, abusive, or fraudulent
                content
              </li>
              <li>Harass or threaten others</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the platform</li>
              <li>
                Reverse-engineer, copy, or exploit the Service except as
                permitted by law
              </li>
              <li>
                Use the Service on behalf of businesses you are not authorized
                to manage
              </li>
            </ul>

            <p>
              We may suspend or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section title="6. Subscription & Billing">
            <p>
              Certain features of RevUShield require a paid subscription.
              Subscriptions may include monthly recurring billing, usage limits,
              trial periods, and feature restrictions based on plan tier.
            </p>

            <p>
              Subscriptions automatically renew unless canceled before the next
              billing date. Payments are processed through third-party payment
              processors such as Stripe. RevUShield does not store full payment
              card information.
            </p>

            <p>
              Failure to pay may result in suspension or termination of access
              to paid features.
            </p>
          </Section>

          <Section title="7. Cancellation">
            <p>
              You may cancel your subscription at any time through your account
              settings or by contacting support. Unless otherwise stated,
              cancellations take effect at the end of the current billing
              period.
            </p>

            <p>
              Fees already paid are generally non-refundable except where
              required by law.
            </p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>
              RevUShield and its related software, branding, content, and
              technology are owned by RevUShield and protected by applicable
              intellectual property laws.
            </p>

            <p>
              You retain ownership of your business information, review data,
              and content you create or publish using the Service. AI-generated
              responses may be used, edited, or published by you without
              restriction.
            </p>

            <p>
              We may use aggregated and anonymized usage data to improve the
              Service.
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              Your use of RevUShield is also governed by our Privacy Policy. By
              using the Service, you acknowledge that internet transmissions and
              third-party services cannot be guaranteed to be completely secure.
            </p>
          </Section>

          <Section title="10. Disclaimer of Warranties">
            <p className="font-semibold uppercase">
              The Service is provided “as is” and “as available” without
              warranties of any kind, express or implied.
            </p>

            <p>
              We do not guarantee that the Service will be uninterrupted or
              error-free, AI responses will be accurate or appropriate,
              third-party services will remain available, or Google APIs or
              features will continue to function without change.
            </p>

            <p>Your use of the Service is at your own risk.</p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p className="font-semibold uppercase">
              To the maximum extent permitted by law, RevUShield shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, including loss of profits, business, data, or
              goodwill.
            </p>

            <p>
              Our total liability for any claim relating to the Service shall
              not exceed the amount you paid to RevUShield during the twelve
              months prior to the event giving rise to the claim.
            </p>
          </Section>

          <Section title="12. Termination">
            <p>
              We may suspend or terminate access to the Service if you violate
              these Terms, your use creates risk or legal exposure, required
              third-party integrations become unavailable, or we discontinue the
              Service.
            </p>

            <p>
              Upon termination, your right to use the Service ends immediately.
              Certain data may remain temporarily in backups or logs, and we may
              delete account data after a reasonable retention period.
            </p>
          </Section>

          <Section title="13. Changes to These Terms">
            <p>
              We may update these Terms from time to time. If material changes
              are made, we may provide notice through the Service, by email, or
              by updating the “Last updated” date above.
            </p>

            <p>
              Continued use of the Service after changes become effective
              constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="14. Governing Law">
            <p>
              These Terms are governed by the laws of the State of Ohio, without
              regard to conflict of law principles. Any disputes relating to
              these Terms or the Service shall be resolved in the courts located
              in Ohio, unless otherwise required by applicable law.
            </p>
          </Section>

          <Section title="15. Contact">
            <p>If you have questions about these Terms, contact:</p>

            <p>
              RevUShield
              <br />
              Email:{" "}
              <a href="mailto:support@revushield.com">support@revushield.com</a>
            </p>
          </Section>
        </div>
      </div>

      <footer className="mt-10 border-t border-slate-200 pt-5 pb-2 text-sm text-slate-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p>© 2026 RevUShield</p>

        <div className="flex flex-wrap items-center gap-4">
          <Link to="/legal/terms" className="hover:text-slate-900 transition">
            Terms
          </Link>

          <Link to="/legal/privacy" className="hover:text-slate-900 transition">
            Privacy
          </Link>

          <a
            href="mailto:support@revushield.com"
            className="hover:text-slate-900 transition"
          >
            Support
          </a>

          <span className="text-slate-400">v1.0</span>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold text-slate-900 mb-3">{title}</h2>

      <div className="space-y-3 text-slate-700">{children}</div>
    </section>
  );
}
